import { useForm } from "react-hook-form";
import {
  PostCandidateAthlete,
  registerAthleteWithEventId,
} from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import Nav from "../../../components/Nav";
import { useParams, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { GetEventsByEventId } from "../../../services/EventsService";
import {
  CheckValidCashToken,
  PostPayment,
} from "../../../services/PaymentService";
import {
  GetEventCombinePrice,
  getCombineBundleForEvent,
} from "../../../services/EventsService";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../store/slices/accountSlice";
import { useNavigate } from "react-router-dom";
import { BackButton } from "../../../components/backButton";
import { CheckIsEmailExist } from "../../../services/AccountService";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import SpotRegisterAthleteStep1 from "./SpotRegisterAthleteStep1";
import SpotRegisterAthleteStep2 from "./SpotRegisterAthleteStep2";

const STEP1_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "confirmEmail",
  "password",
  "confirmPassword",
  "athletePhoneNumber",
  "parentPhoneNumber",
  "primaryPosition",
  "expectedYearOfGraduation",
];

const stripPosition = (position) => {
  if (!position) return "";

  return position.includes("(") ? position.split("(")[0].trim() : position;
};

const RegisterAthlete = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const apiInstance = useAxiosInstance();
  const [eventNameSelected, setEventNameSelected] = useState();
  const [cashTokenNew, setCashTokenNew] = useState("");
  const [combinePrice, setCombinePrice] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [step, setStep] = useState(1);
  const trackEvent = useTrackEvent();
  const step1PostRef = useRef(null);

  useEffect(() => {
    trackEvent("Registration0_EventsPage", { event_id: eventId });
  }, []); // eslint-disable-line

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, eventId);

        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
          trackEvent("Registration1_OpenForm", {
            event_id: eventId,
            event_name: data.name,
            event_date: data.date,
          });
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadEvents();
  }, [apiInstance, eventId, trackEvent]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const cashToken = searchParams.get("cashToken");

    if (cashToken) {
      setCashTokenNew(cashToken);
      trackEvent("Registration3_5_AppliedCoupon", { event_id: eventId });
    }
  }, [location.search, eventId, trackEvent]);

  useEffect(() => {
    const fetchPricingOptions = async () => {
      try {
        const response = await GetEventCombinePrice(apiInstance, eventId);

        if (response.data) {
          setCombinePrice(response?.data?.combinePrice);
          trackEvent("Registration3_ViewedPricing", {
            event_id: eventId,
            combine_price: response?.data?.combinePrice,
          });
        }
      } catch (error) {}
    };

    fetchPricingOptions();
  }, []); // eslint-disable-line

  const validateCashToken = async (token) => {
    try {
      const response = await CheckValidCashToken(apiInstance, token);

      if (response.success === true) {
        return true;
      } else {
        toast.error("Invalid cash token.", { position: "top-right" });

        return false;
      }
    } catch (error) {
      toast.error("Failed to validate cash token.", { position: "top-right" });
    }
  };

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);

      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-right",
        });

        return true;
      }

      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-right",
      });

      return true;
    }
  };

  const {
    register,

    handleSubmit,

    reset,

    watch,

    control,

    trigger,
    getValues,
    setValue,

    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",

      lastName: "",

      email: "",

      confirmEmail: "",

      password: "",

      confirmPassword: "",

      age: 0,

      gpa: "",

      highSchool: "",

      expectedYearOfGraduation: "",

      primaryPosition: "",

      secondaryPosition: "",

      sportsParticipation: null,

      city: "",

      state: "",

      universityOfInteres: "",

      academicDegreeOfInteres: "",

      role: "spotcombine",

      athletePhoneNumber: "",

      parentPhoneNumber: "",

      varsityYears: null,

      offersReceived: null,

      athleteOffers: null,

      hasGraduated: false,

      major: "",

      yearsLeftToPlay: "",

      redShirtAvailable: false,

      personHelpingYouName: "",

      personHelpingYouRelationship: "",

      personHelpingYouPhone: "",
      universitiesAttended: [{ year: "", school: "", className: "" }],
      tShirtSize: "",
    },

    mode: "onChange",

    reValidateMode: "onChange",

    criteriaMode: "all",
  });

  const hasGraduated = watch("hasGraduated");

  useEffect(() => {
    if (!hasGraduated) {
      setValue("major", ""); // Clear the Major field
    }
  }, [hasGraduated, setValue]);

  const goToStep2 = async () => {
    const isStepValid = await trigger(STEP1_FIELDS, { shouldFocus: true });

    if (!isStepValid) {
      toast.error("Please fix all validation errors before continuing.", {
        position: "top-right",
      });

      return;
    }

    const values = getValues();

    const isEmailExist = await checkIsEmailExist(values.email);

    if (isEmailExist) {
      return;
    }

    const step1Payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      athletePhoneNumber: values.athletePhoneNumber,
      parentPhoneNumber: values.parentPhoneNumber,
      primaryPosition: stripPosition(
        values.primaryPosition === "None" ? "" : values.primaryPosition,
      ),
      expectedYearOfGraduation: Number(values.expectedYearOfGraduation) || 0,
      eventId: eventId,
    };

    step1PostRef.current = PostCandidateAthlete(
      apiInstance,
      step1Payload,
    ).catch(() => {});

    trackEvent("Registration1_5_Step1Submitted", { event_id: eventId });

    setStep(2);
  };

  const goToStep1 = () => {
    setStep(1);
  };

  const onSubmit = async (data) => {
    setSubmitAttempted(true);

    // Trigger validation for all fields

    const isFormValid = await trigger(undefined, { shouldFocus: true });

    if (!isFormValid) {
      toast.error("Please fix all validation errors before submitting.", {
        position: "top-right",
      });

      return;
    }

    const {
      confirmEmail: _ce,
      confirmPassword: _cp,
      ...submittableData
    } = data;

    const sanitizedData = {
      ...submittableData,

      age: Number(data.age) || 0,

      expectedYearOfGraduation: Number(data.expectedYearOfGraduation) || 0,

      varsityYears: Number(data.varsityYears) || 0,

      universitiesAttended: (data.universitiesAttended || [])
        .filter(
          (u) => u.school?.trim() && u.year?.trim() && u.className?.trim(),
        )
        .map((u) => ({
          ...u,
          year: Number(u.year) || 0,
          school: u.school.trim(),
          className: u.className.trim(),
        })),

      athleteOffers: Array.isArray(data.athleteOffers)
        ? data.athleteOffers
        : [],

      sportsParticipation: data.sportsParticipation || "",

      primaryPosition: stripPosition(
        data.primaryPosition === "None" ? "" : data.primaryPosition,
      ),

      secondaryPosition: stripPosition(
        data.secondaryPosition === "None" ? "" : data.secondaryPosition,
      ),

      yearsLeftToPlay: data.yearsLeftToPlay
        ? parseInt(data.yearsLeftToPlay)
        : null,

      redShirtAvailable: Boolean(data.redShirtAvailable),

      hasGraduated: data.hasGraduated,
      major: hasGraduated ? data.major : "",
    };

    const { password: _pw, ...postCandidatePayload } = sanitizedData;

    await step1PostRef.current;

    await PostCandidateAthlete(apiInstance, {
      ...postCandidatePayload,
      eventId: eventId,
    })
      .then(() => {
        trackEvent("Registration2_CompletedForm", {
          event_id: eventId,
        });
      })
      .catch(() => {});

    setLoading(true);

    try {
      if (cashTokenNew) {
        const isValid = await validateCashToken(cashTokenNew);

        if (!isValid) {
          toast.error("Invalid or expired cash token.", {
            position: "top-right",
          });

          return;
        }

        const registrationResponse = await registerAthleteWithEventId(
          apiInstance,

          sanitizedData,

          eventId,
        );

        if (registrationResponse.status === 200) {
          const userId = registrationResponse.data.id;

          const paymentPayload = {
            userId: userId,

            amount: combinePrice,

            isCombine: true,

            isPositionDrill: false,

            isAnnualSubscription: false,

            isMonthlySubscription: false,

            isCash: true,

            eventId: eventId,

            transactionId: "",

            intellipayToken: "",
          };

          await PostPayment(apiInstance, paymentPayload);

          trackEvent("Registration5_RegistrationCompleted", {
            event_id: eventId,
            payment_method: "cash",
            amount: combinePrice,
          });
          setLoading(false);

          toast.success("Athlete registration is successful!", {
            position: "top-right",
          });

          navigate("/register/registrationsuccess");
        } else {
          trackEvent("Registration4_5_PaymentFailed", {
            event_id: eventId,
            reason: registrationResponse?.error ?? "unknown",
          });
          toast.error("Registration failed: " + registrationResponse.error, {
            position: "top-right",
          });

          return;
        }
      } else {
        const updatedData = { ...sanitizedData, eventId: eventId };

        dispatch(clearRegisterData());

        dispatch(registerAccountStore(updatedData));

        localStorage.clear();

        localStorage.setItem("registeredAccount", JSON.stringify(updatedData));

        const bundleResponse = await getCombineBundleForEvent(
          apiInstance,
          eventId,
        );
        const bundle =
          bundleResponse.success &&
          Array.isArray(bundleResponse.data?.combines) &&
          bundleResponse.data.combines.length > 0
            ? bundleResponse.data
            : null;

        setLoading(false);

        if (bundle) {
          navigate("/athlete-combine-bundle", {
            state: { fromSpotRegister: true, eventId, bundle },
          });
        } else {
          trackEvent("Registration4_OpenedPaymentModal", { event_id: eventId });
          navigate("/athlete-membership", {
            state: { fromSpotRegister: true },
          });
        }

        reset();

        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
      }
    } catch (error) {
      toast.error("Error Registering combine Athlete: " + error.error, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if field should show error

  const shouldShowError = (fieldName) => {
    return submitAttempted || errors[fieldName];
  };

  return (
    <>
      <Nav />

      <div className="w-full max-w-5xl p-6 mx-auto pt-10">
        <div className=" hidden"> {!cashTokenNew && <BackButton />}</div>

        <div className="flex items-center justify-center gap-4 mb-2 text-sm text-gray-300">
          <span className={step === 1 ? "font-bold text-white" : ""}>
            Step 1 of 2
          </span>
          <span>&rarr;</span>
          <span className={step === 2 ? "font-bold text-white" : ""}>
            Step 2 of 2
          </span>
        </div>

        <form
          className={`athleteregistration-${process.env.REACT_APP_ENVIRONMENT_NAME} container mx-auto `}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          {step === 1 ? (
            <SpotRegisterAthleteStep1
              register={register}
              errors={errors}
              shouldShowError={shouldShowError}
              watch={watch}
              eventNameSelected={eventNameSelected}
              onNext={goToStep2}
              loading={loading}
            />
          ) : (
            <SpotRegisterAthleteStep2
              register={register}
              errors={errors}
              shouldShowError={shouldShowError}
              watch={watch}
              control={control}
              hasGraduated={hasGraduated}
              loading={loading}
              onBack={goToStep1}
            />
          )}
        </form>
      </div>
    </>
  );
};

export default RegisterAthlete;
